/* Configured LLM providers, their models, and which one serves the tenant.
 *
 * **Every write invalidates rather than patching optimistically**, for `serving.ts`'s reason: the
 * server refuses things — an untested model cannot be activated, a routed model cannot be deleted, an
 * air-gapped tenant cannot reach a hosted provider — and the authoritative answer is the one it
 * stored. An optimistic patch would show a routing the platform does not have.
 *
 * **Credentials pass through this file and are kept nowhere.** A write body carries them once; React
 * Query does not cache mutation variables after settling, nothing here stores them, and no response
 * returns them — `credential.fields` lists names.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isConnectionTestResult,
  isDeletion,
  isModelConfigView,
  isProviderCatalog,
  isProviderConfigList,
  isProviderConfigView,
  isRoutingView,
} from '@/shared/api/generated/ai-serving';
import type {
  AuthMethod,
  ConnectionTestResult,
  Deletion,
  ModelConfigView,
  ProviderCatalog,
  ProviderConfigList,
  ProviderConfigView,
  ProviderKind,
  RoutingView,
} from '@/shared/api/generated/ai-serving';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  put,
  removeOne,
} from '@/shared/api/http';

const BASE = '/v1/ai/serving';
export const CATALOG_URL = `${BASE}/providers`;
export const CONFIGS_URL = `${BASE}/provider-configs`;
export const ROUTING_URL = `${BASE}/routing`;

const KEY = ['ai', 'serving', 'llm'] as const;

export interface ProviderWrite {
  readonly kind: ProviderKind;
  readonly display_name: string;
  readonly auth_method: AuthMethod;
  readonly settings: Readonly<Record<string, string>>;
  /** Only the fields typed into this submission. Empty keeps the stored credential. */
  readonly credentials: Readonly<Record<string, string>>;
  readonly enabled: boolean;
}

export interface ModelWrite {
  readonly display_name: string;
  readonly model_identifier: string;
  readonly deployment_name: string;
  readonly parameters: Readonly<Record<string, number>>;
  readonly enabled: boolean;
}

export function useProviderCatalog(): UseQueryResult<ProviderCatalog> {
  return useQuery({
    queryKey: [...KEY, 'catalog'],
    queryFn: () => getOne(CATALOG_URL, isProviderCatalog, 'provider catalog'),
    staleTime: Infinity,
    retry: false,
  });
}

export function useProviderConfigs(): UseQueryResult<ProviderConfigList> {
  return useQuery({
    queryKey: [...KEY, 'configs'],
    queryFn: () => getOne(CONFIGS_URL, isProviderConfigList, 'provider configurations'),
    retry: false,
  });
}

export function useRouting(): UseQueryResult<RoutingView> {
  return useQuery({
    queryKey: [...KEY, 'routing'],
    queryFn: () => getOne(ROUTING_URL, isRoutingView, 'model routing'),
    retry: false,
  });
}

function useInvalidate(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: KEY });
    /* The legacy route read describes the same row, and the audit history records the change. */
    void client.invalidateQueries({ queryKey: ['ai', 'serving', 'route'] });
    void client.invalidateQueries({ queryKey: ['audit', 'entries'] });
  };
}

export function useCreateProvider(): UseMutationResult<
  ProviderConfigView,
  Error,
  ProviderWrite
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (write: ProviderWrite) =>
      post(
        CONFIGS_URL,
        idempotencyKeyFor('llm-provider-create', write.display_name),
        isProviderConfigView,
        'provider configuration',
        write,
      ),
    onSuccess: (_, write) => {
      forgetIdempotencyKey('llm-provider-create', write.display_name);
      invalidate();
    },
  });
}

export function useUpdateProvider(): UseMutationResult<
  ProviderConfigView,
  Error,
  { readonly id: string; readonly write: ProviderWrite }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, write }) =>
      put(`${CONFIGS_URL}/${id}`, isProviderConfigView, 'provider configuration', write),
    onSuccess: invalidate,
  });
}

export function useDeleteProvider(): UseMutationResult<Deletion, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => removeOne(`${CONFIGS_URL}/${id}`, isDeletion, 'deletion'),
    onSuccess: invalidate,
  });
}

/* A test is a fresh request every time somebody presses the button, so its key is minted and
 * forgotten per attempt — a retry of a lost response reuses it, a second press does not. */
function testOnce(
  url: string,
  scope: string,
  subject: string,
): Promise<ConnectionTestResult> {
  return post(
    url,
    idempotencyKeyFor(scope, subject),
    isConnectionTestResult,
    'connection test',
  ).finally(() => {
    forgetIdempotencyKey(scope, subject);
  });
}

export function useTestProvider(): UseMutationResult<
  ConnectionTestResult,
  Error,
  string
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      testOnce(`${CONFIGS_URL}/${id}/test`, 'llm-provider-test', id),
    onSettled: invalidate,
  });
}

export function useTestModel(): UseMutationResult<ConnectionTestResult, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      testOnce(`${BASE}/models/${id}/test`, 'llm-model-test', id),
    onSettled: invalidate,
  });
}

export function useCreateModel(): UseMutationResult<
  ModelConfigView,
  Error,
  { readonly providerId: string; readonly write: ModelWrite }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ providerId, write }) =>
      post(
        `${CONFIGS_URL}/${providerId}/models`,
        idempotencyKeyFor('llm-model-create', `${providerId}:${write.model_identifier}`),
        isModelConfigView,
        'model configuration',
        write,
      ),
    onSuccess: (_, { providerId, write }) => {
      forgetIdempotencyKey('llm-model-create', `${providerId}:${write.model_identifier}`);
      invalidate();
    },
  });
}

export function useUpdateModel(): UseMutationResult<
  ModelConfigView,
  Error,
  { readonly id: string; readonly write: ModelWrite }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, write }) =>
      put(`${BASE}/models/${id}`, isModelConfigView, 'model configuration', write),
    onSuccess: invalidate,
  });
}

export function useDeleteModel(): UseMutationResult<Deletion, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => removeOne(`${BASE}/models/${id}`, isDeletion, 'deletion'),
    onSuccess: invalidate,
  });
}

export function useActivateModel(): UseMutationResult<RoutingView, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (modelId: string) =>
      put(`${ROUTING_URL}/active`, isRoutingView, 'model routing', { model_id: modelId }),
    onSuccess: invalidate,
  });
}

export function useSetRoutingEnabled(): UseMutationResult<RoutingView, Error, boolean> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      put(`${ROUTING_URL}/enabled`, isRoutingView, 'model routing', { enabled }),
    onSuccess: invalidate,
  });
}

export function useSetFallback(): UseMutationResult<
  RoutingView,
  Error,
  { readonly modelId: string | null; readonly enabled: boolean }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ modelId, enabled }) =>
      put(`${ROUTING_URL}/fallback`, isRoutingView, 'model routing', {
        model_id: modelId,
        enabled,
      }),
    onSuccess: invalidate,
  });
}

export type {
  AuthMethod,
  ConnectionTestResult,
  ModelConfigView,
  ProviderConfigView,
  ProviderKind,
  RoutingView,
};
